import {useCookies} from '@optimaxdev/utils';

import FormData from 'form-data';

import {OrderErrorType} from 'constants/constants';
import {flags} from 'libs/rollout';
import {cookieStore} from 'libs/storage/storage';
import {SG_LENS_PACKAGE_TITLE} from 'mobile/pages/success/components/successPageForGlassesRedesign/components/orderDetails/components/glassesItemUpgrades/constants';

import {getAkamaiCountryAndCity} from './akamai/adapter';

/**
 * A function that returns an array of user location data using Akamai
 * @return {string[]} - location dataset
 */
export const getGeolocationByAkamai = (): string[] =>
    cookieStore.get('x-akamai-geo', '').split(',');

/**
 * Disable scroll on page
 * @param {boolean} state - current state
 */
export const disableScroll = (state: boolean) => {
    document.body.style.overflow = state ? 'hidden' : '';
};

/**
 * Escape special characters
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
 *
 * @export
 * @param {string} stringValue - string with a special characters
 * @returns {string} - return string with a special characters escaped
 */
export function escapeRegExp(stringValue: string): string {
    return stringValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Return string with upper first char
 * @param {string} value - source string
 * @return {string} - string with upper first char
 */
export const capitalize = (value: string): string => {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

/**
 * get new title for packages when it is sunglasses and dPDPsgPackagesUI is true
 * of return old title
 */
export const getPackageTitle = (sku: string, title: string, isSunglasses: boolean) => {
    if (isSunglasses && flags.dPDPsgPackagesUI.isEnabled()) {
        return SG_LENS_PACKAGE_TITLE[sku as keyof typeof SG_LENS_PACKAGE_TITLE] || title;
    }
    return title;
};

/**
 * A hook that returns is the user from Canada (CA code) based on akamai cookie
 * @return {Boolean} - boolean according user country
 */
export const useCheckCanadaByAkamai = (): boolean => {
    const [cookies] = useCookies();
    return getAkamaiCountryAndCity(cookies['x-akamai-geo'] || '')[0] === 'CA';
};

/**
 * Serialize object to FromData
 * @param {{}} data - Object for serializing
 * @param {string} prefix - Prefix key
 * @param {FormData} [temp = new FormData] - Intermediate result of FormData
 * @return {FormData} - Result of FormData
 */
export function serializeToFormData(
    data: any,
    prefix?: string,
    temp: FormData = new FormData(),
): FormData {
    let result = temp;

    Object.keys(data).forEach((dataKey: string) => {
        const key = prefix ? `${prefix}[${dataKey}]` : dataKey;
        const value = data[dataKey];

        if (value !== null && typeof value === 'object') {
            if (value instanceof File || value instanceof Blob) {
                result.append(key, value);
            } else {
                result = serializeToFormData(value, key, result);
            }
        } else if (typeof value !== 'undefined') {
            result.append(key, value);
        }
    });

    return result;
}

/**
 * Extract the MIME type from a base64 string using a regular expression.
 * @param {string} encoded - base64 string
 * @returns {string | null} - mime type
 */
export const getMimeTypeFromBase64 = (encoded?: string): string | null => {
    let result: string | null = null;

    if (typeof encoded !== 'string' || !encoded.length) {
        return result;
    }

    const [, mime] = encoded.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/) || [];

    if (mime && mime.length) {
        result = mime;
    }
    return result;
};

type ExtractErrorsType = {
    message: string;
    title: string;
}[];

/**
 * Extract error messages from rejected response from server
 * @param {{}} response - rejected response from server
 * @returns {Array<string>} error messages
 */
export function extractErrorMessagesFromResponse(response: {} = {}): Array<OrderErrorType> {
    // @ts-expect-error type of response
    const errors: ExtractErrorsType = response?.data?.errors || [];
    // @ts-expect-error type of response
    const errorMessages: ExtractErrorsType = response.data?.messages?.error || [];
    return errors.length
        ? errors
        : errorMessages.map(
              (error: {message?: string}): OrderErrorType => ({title: error?.message ?? ''}),
          );
}
